import { inject, Injectable } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { Status } from '@df/core/status';
import { BehaviorSubject } from 'rxjs';
import { CustomerTokenService } from './customer-token.service';

export interface IUserImagesResponse {
    [key: string]: string;
}

export enum UserImageRole {
    Profile = 'profile',
    Outfit = 'outfit',
    FamilyMember = 'family_member',
    Horse = 'horse'
}

@Injectable({
    providedIn: 'root'
})
export class UserImagesService {
    private apiRequestService = inject(RequestService);
    private customerTokenService = inject(CustomerTokenService);
    readonly userImages = new BehaviorSubject<IUserImagesResponse>({});

    /**
     * Load user images from API
     */
    async loadUserImages(loyaltyId: string): Promise<IUserImagesResponse> {
        try {
            const response = await this.apiRequestService.send(undefined, {
                path: `user-images/${loyaltyId}`,
                requestOptions: {
                    method: 'GET',
                    api: 'node'
                }
            });
            this.userImages.next(response || {});
            return this.userImages.value;
        } catch (error) {
            console.error('Error loading user images:', error);
            this.userImages.next({});
            return this.userImages.value;
        }
    }

    /**
     * Saves user image to the server
     * Automatically processes image orientation before saving to fix iOS rotation issues
     */
    async saveUserImage(
        loyaltyId: string,
        imageRole: UserImageRole,
        file: File,
        status?: Status,
        index?: number
    ): Promise<IUserImagesResponse> {
        try {
            // Append index to imageRole if provided
            const finalImageRole = index !== undefined ? `${imageRole}_${index}` : imageRole;

            const token = await this.customerTokenService.retrieveToken();
            const response = await this.apiRequestService.send(
                status,
                {
                    path: 'user-images/save',
                    requestOptions: {
                        method: 'POST',
                        api: 'node'
                    },
                    data: {
                        file: file,
                        imageRole: finalImageRole,
                        loyaltyId: loyaltyId
                    }
                },
                undefined,
                undefined,
                { fileUpload: true, token }
            );

            // Update user images cache with response data
            if (response) {
                this.userImages.next(response);
            }

            return this.userImages.value;
        } catch (error) {
            if (status) {
                status.error = 'Failed to process or upload image';
            }
            // Re-throw the error so calling components can handle it
            throw error;
        }
    }

    /**
     * Gets user image by role (returns first image if multiple exist)
     */
    getUserImage(role: UserImageRole, index?: number): string | undefined {
        const roleKey = index !== undefined ? `${role}_${index}` : role;
        const fileName = this.userImages.value[roleKey];

        if (!fileName) {
            return undefined;
        }

        return `/static/user-images/${fileName}`;
    }

    /**
     * Clear user images cache
     */
    clearCache(): void {
        this.userImages.next({});
    }

    /**
     * Get current cached images
     */
    get images(): IUserImagesResponse {
        return this.userImages.value;
    }

    /**
     * Normalize an image file to bake orientation and avoid size inflation.
     * - Keeps PNG as-is (re-encoding often inflates size)
     * - For JPEG/HEIC/HEIF: converts to JPEG and searches for a quality that
     *   does not exceed min(5MB, original size)
     */
    async normalizeImage(file: File): Promise<File> {
        // Read to data URL first
        const dataUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });

        // Draw onto canvas to bake any orientation into pixels
        const img = new Image();
        img.src = dataUrl;
        await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = reject;
        });

        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return file; // Fallback
        }
        ctx.drawImage(img, 0, 0);

        // We never want to increase the file size. Cap to the smaller of 5MB or original size.
        const MAX_SIZE = 5 * 1024 * 1024; // 5MB
        const targetSize = Math.min(MAX_SIZE, file.size);

        // If original is PNG, re-encoding via canvas often inflates size; keep original PNG.
        if (file.type?.toLowerCase().startsWith('image/png')) {
            return file;
        }

        // For JPEG (and HEIC/HEIF which we convert to JPEG), choose a quality that does not exceed targetSize
        const outType: string = 'image/jpeg';

        const toBlob = (quality: number): Promise<Blob> =>
            new Promise((resolve, reject) => {
                canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('Failed to encode image'))), outType, quality);
            });

        const qualities = [1, 0.96, 0.92, 0.88, 0.84, 0.8, 0.76, 0.72, 0.68];
        let bestBlob: Blob | null = null;
        for (const q of qualities) {
            const blob = await toBlob(q);
            bestBlob = blob;
            if (blob.size <= targetSize) {
                break;
            }
        }

        const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg').replace(/\.(png)$/i, '.jpg');
        return bestBlob ? new File([bestBlob], newName, { type: outType }) : file;
    }
}
